import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import DocumentMeta from 'react-document-meta';
import PersonalityHeader from '../components/Header';
import { findCharacter } from '../data/characters';
import { typeTabContent } from '../data/typeTabContent';
import Styles from './styles.module.css';

// 円状配置の順番(4bit二進順)。対角(インデックス+8)は必ず
// 「needsAwareness(4軸すべて逆)」のタイプになるよう並べてある。
const ORDER = [
  'HSTJ', 'HSTP', 'HSEJ', 'HSEP', 'HRTJ', 'HRTP', 'HREJ', 'HREP',
  'CREP', 'CREJ', 'CRTP', 'CRTJ', 'CSEP', 'CSEJ', 'CSTP', 'CSTJ',
];

const INDEX_OF: Record<string, number> = {};
ORDER.forEach((code, i) => { INDEX_OF[code] = i; });

const CX = 250;
const CY = 250;
const RADIUS = 190;

function posOf(index: number) {
  const angle = -Math.PI / 2 + index * ((2 * Math.PI) / 16);
  return { x: CX + RADIUS * Math.cos(angle), y: CY + RADIUS * Math.sin(angle) };
}

function hamming(a: string, b: string) {
  let n = 0;
  for (let i = 0; i < 4; i += 1) {
    if (a[i] !== b[i]) n += 1;
  }
  return n;
}

type RelationKind = 'self' | 'natural' | 'broaden' | 'aware' | 'other';

function relationOf(mainCode: string, otherCode: string): { kind: RelationKind; dist?: number } {
  if (otherCode === mainCode) return { kind: 'self' };
  const content = typeTabContent[mainCode];
  if (content.naturalFit.some((m) => m.type === otherCode)) return { kind: 'natural' };
  if (content.broadening.some((m) => m.type === otherCode)) return { kind: 'broaden' };
  if (content.needsAwareness.some((m) => m.type === otherCode)) return { kind: 'aware' };
  return { kind: 'other', dist: hamming(mainCode, otherCode) };
}

// 既存の性格診断結果ページ(result/index.tsx の GROUP_ACCENTS)と同じ配色に揃える。
const RELATION_COLOR: Record<'natural' | 'broaden' | 'aware', { tint: string; accent: string }> = {
  natural: { tint: '#E6F4EA', accent: '#3D9A5C' },
  broaden: { tint: '#E7F0FB', accent: '#3E7BC4' },
  aware: { tint: '#FBF0DE', accent: '#C98A2E' },
};

const VERDICT: Record<'natural' | 'broaden' | 'aware' | 'other', { emoji: string; title: string; text: string }> = {
  natural: {
    emoji: '🎉',
    title: '相性ばっちり!自然に合うタイプ',
    text: 'お互いのペースが近く、自然と息が合いやすい組み合わせです。',
  },
  broaden: {
    emoji: '🌱',
    title: '視野を広げ合えるタイプ',
    text: 'ちょっと違うところがあるからこそ、新しい発見や気づきをもらえそうです。',
  },
  aware: {
    emoji: '⚡',
    title: '正反対タイプ!でも学び合える',
    text: '4つの軸がすべて逆の、対角線上にあるタイプです。違いを面白がれると良い関係に。',
  },
  other: {
    emoji: '🙂',
    title: '一部が似ているタイプ',
    text: '直接の相性データはありませんが、4つの軸のうち{n}つが違うタイプです。',
  },
};

const PersonalityAnalytics = () => {
  const navigate = useNavigate();
  const [mainCode, setMainCode] = useState('HSTJ');
  const [partnerCode, setPartnerCode] = useState<string | null>(null);

  const content = typeTabContent[mainCode];
  const mainPos = posOf(INDEX_OF[mainCode]);

  const edges = useMemo(() => {
    const list: { code: string; kind: 'natural' | 'broaden' | 'aware' }[] = [];
    content.naturalFit.forEach((m) => list.push({ code: m.type, kind: 'natural' }));
    content.broadening.forEach((m) => list.push({ code: m.type, kind: 'broaden' }));
    content.needsAwareness.forEach((m) => list.push({ code: m.type, kind: 'aware' }));
    return list;
  }, [content]);

  const selectMain = (code: string) => {
    setMainCode(code);
    setPartnerCode(null);
  };

  const selectPartner = (code: string) => {
    if (code === mainCode) return;
    setPartnerCode(code);
  };

  const openDetail = (code: string) => {
    navigate(`/personality/result/${code}`, { state: { introduce: true } });
  };

  const partnerRelation = partnerCode ? relationOf(mainCode, partnerCode) : null;
  const partnerChar = partnerCode ? findCharacter(partnerCode) : undefined;
  const verdict = partnerRelation && partnerRelation.kind !== 'self'
    ? VERDICT[partnerRelation.kind as 'natural' | 'broaden' | 'aware' | 'other']
    : null;

  const meta = {
    title: 'タイプ相性マップ | 性格診断 | memorii works',
    description: '性格診断16タイプの相性を、タップして確かめられる相性マップです。授業・紹介での利用を想定しています。',
    canonical: 'https://memorii-works.com/personality/analytics',
    meta: { charset: 'utf-8' },
  };

  return (
    <DocumentMeta {...meta}>
      <PersonalityHeader title="タイプ相性マップ" mode="back" />
      <div className={Styles.page}>
        <p className={Styles.lead}>気になるタイプを2つタップして、相性を見てみよう。</p>

        <div className={Styles.sectionLabel}>① 自分のタイプ</div>
        <div className={Styles.grid}>
          {ORDER.map((code) => {
            const c = findCharacter(code);
            if (!c) return null;
            return (
              <button
                key={code}
                type="button"
                className={`${Styles.chip} ${code === mainCode ? Styles.chipActive : ''}`}
                onClick={() => selectMain(code)}
              >
                <img src={c.imagePath} alt={code} className={Styles.chipImg} />
                <span className={Styles.chipCode}>{code}</span>
              </button>
            );
          })}
        </div>

        <div className={Styles.sectionLabel}>② 気になる相手のタイプ</div>

        <div
          className={Styles.verdict}
          style={verdict ? { backgroundColor: RELATION_COLOR[partnerRelation!.kind as 'natural' | 'broaden' | 'aware']?.tint ?? '#F5F5F5' } : undefined}
        >
          <span className={Styles.verdictEmoji}>{verdict ? verdict.emoji : '👉'}</span>
          <div>
            <p className={Styles.verdictTitle}>
              {verdict && partnerCode && partnerChar
                ? `${partnerCode}(${partnerChar.subtitle})との相性 ー ${verdict.title}`
                : '円の中から相手のタイプをタップしてね'}
            </p>
            <p className={Styles.verdictText}>
              {verdict
                ? verdict.text.replace('{n}', String(partnerRelation?.dist ?? ''))
                : '結果がここに表示されます。'}
            </p>
          </div>
        </div>

        <div className={Styles.mapWrap}>
          <svg className={Styles.mapSvg} viewBox="0 0 500 500">
            {edges.map((e) => {
              const p = posOf(INDEX_OF[e.code]);
              const focus = partnerCode === e.code;
              return (
                <line
                  key={e.code}
                  x1={mainPos.x}
                  y1={mainPos.y}
                  x2={p.x}
                  y2={p.y}
                  stroke={RELATION_COLOR[e.kind].accent}
                  strokeWidth={focus ? 6 : 3}
                  strokeOpacity={focus ? 1 : 0.3}
                  strokeLinecap="round"
                />
              );
            })}
            {partnerCode && partnerRelation?.kind === 'other' && (
              <line
                x1={mainPos.x}
                y1={mainPos.y}
                x2={posOf(INDEX_OF[partnerCode]).x}
                y2={posOf(INDEX_OF[partnerCode]).y}
                stroke="#9AA3AC"
                strokeWidth={4}
                strokeDasharray="3,7"
                strokeLinecap="round"
              />
            )}
          </svg>
          {ORDER.map((code, i) => {
            const c = findCharacter(code);
            if (!c) return null;
            const p = posOf(i);
            const isMain = code === mainCode;
            const isPartner = code === partnerCode;
            const rel = isMain ? null : relationOf(mainCode, code);
            const relColor = rel && (rel.kind === 'natural' || rel.kind === 'broaden' || rel.kind === 'aware')
              ? RELATION_COLOR[rel.kind]
              : null;
            return (
              <button
                key={code}
                type="button"
                className={Styles.node}
                style={{
                  left: `${(p.x / 500) * 100}%`,
                  top: `${(p.y / 500) * 100}%`,
                  borderColor: isMain ? '#1B1B1B' : relColor ? relColor.accent : '#EEEEEE',
                  borderWidth: isMain || isPartner ? 4 : relColor ? 3 : 2,
                  backgroundColor: relColor ? relColor.tint : '#ffffff',
                }}
                onClick={() => selectPartner(code)}
                aria-label={code}
              >
                <img src={c.imagePath} alt={code} className={Styles.nodeImg} />
              </button>
            );
          })}
        </div>

        <div className={Styles.legend}>
          <span><i className={Styles.dotNatural} />自然に合う</span>
          <span><i className={Styles.dotBroaden} />視野を広げる</span>
          <span><i className={Styles.dotAware} />正反対(ちょっと注意)</span>
        </div>

        {partnerCode && (
          <button type="button" className={Styles.detailLink} onClick={() => openDetail(partnerCode)}>
            {partnerCode}のくわしい紹介を見る →
          </button>
        )}

        <p className={Styles.footerNote}>
          ※ 4つの軸(H/C・S/R・T/E・J/P)がどれだけ同じ/違うかから見た、ひとつの相性の見方です。
          「相性が悪い」という意味ではなく、どちらのタイプにも良さがあります。
        </p>
      </div>
    </DocumentMeta>
  );
};

export default PersonalityAnalytics;
